import { useQuery, useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { applicantService } from '@/services/applicant.service'
import { useState, useRef } from 'react'
import { ApiError } from '@/lib/api-client'
import { useConfirm } from '@/hooks/useConfirm'
import { BookInterviewPage } from './BookInterviewPage'
import promoVideo from '@/assets/videos/ulikeit.mp4'
import logo from '@/assets/logo.svg'

export function ApplicantDashboardPage() {
  const { t } = useTranslation()
  const { user, token, clearAuth } = useAuthStore()
  const { confirm, modal: confirmModal } = useConfirm()
  const [uploadProgress, setUploadProgress] = useState<string>('')
  const [qualificationChoice, setQualificationChoice] = useState<'video' | 'interview' | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch applicant details
  const { data: applicant, isLoading, refetch } = useQuery({
    queryKey: ['applicant', user?.id],
    queryFn: () => applicantService.getApplicantDetails(user!.id, token!),
    enabled: !!user && !!token,
  })

  // Fetch latest script version
  const { data: latestVersion } = useQuery({
    queryKey: ['latest-script-version'],
    queryFn: () => applicantService.getLatestScriptVersion(token!),
    enabled: !!token,
  })

  // Script view handler
  const handleViewScript = (version: string) => {
    // Track download
    applicantService.getScriptDownloadUrl(version, token!).then(() => {
      refetch()
    }).catch(err => {
      console.error('Failed to track script download:', err)
    })

    // Navigate to viewer
    window.location.href = `/applicant/script?version=${version}`
  }

  // Video upload mutation
  const videoUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadProgress(t('applicant.gettingUploadUrl'))

      // Get presigned URL
      const { uploadUrl, fileUrl } = await applicantService.getVideoUploadUrl(
        {
          fileName: file.name,
          contentType: file.type,
          uploadType: 'video',
        },
        token!
      )

      setUploadProgress(t('applicant.uploadingVideo'))

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      })

      if (!uploadResponse.ok) {
        throw new Error('Upload failed')
      }

      setUploadProgress(t('applicant.confirmingUpload'))

      // Confirm upload
      await applicantService.confirmVideoUpload(user!.id, fileUrl, token!)

      setUploadProgress(t('applicant.uploadComplete'))
      return fileUrl
    },
    onSuccess: () => {
      refetch()
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setTimeout(() => setUploadProgress(''), 3000)
    },
    onError: (error) => {
      setUploadProgress(
        error instanceof ApiError ? error.message : 'Upload failed'
      )
    },
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('video/')) {
        await confirm({
          title: t('common.error'),
          message: t('applicant.pleaseSelectVideo'),
          confirmLabel: 'OK',
        })
        return
      }
      if (file.size > 100 * 1024 * 1024) {
        await confirm({
          title: t('common.error'),
          message: t('applicant.fileTooLarge'),
          confirmLabel: 'OK',
        })
        return
      }
      videoUploadMutation.mutate(file)
    }
  }

  const handleLogout = () => {
    clearAuth()
    window.location.href = '/'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
              <img src={logo} alt="U-Like-It" className="h-10 w-auto hover:opacity-80 transition-opacity" />
            </a>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('applicant.dashboard')}</h1>
              <p className="text-sm text-gray-600">
                {t('applicant.welcome', { name: `${user?.firstName} ${user?.lastName}` })}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
          >
            {t('auth.logout')}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('applicant.applicationStatus')}</h2>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              applicant?.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
              applicant?.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
              applicant?.status === 'REVISION_REQUIRED' ? 'bg-yellow-100 text-yellow-800' :
              applicant?.status === 'UNDER_REVIEW' ? 'bg-purple-100 text-purple-800' :
              applicant?.status === 'INTERVIEW_SCHEDULED' ? 'bg-indigo-100 text-indigo-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {({
                REGISTERED: t('applicant.statusRegistered'),
                VIDEO_UPLOADED: t('applicant.statusVideoUploaded'),
                INTERVIEW_SCHEDULED: 'Interview vereinbart',
                UNDER_REVIEW: t('applicant.statusUnderReview'),
                APPROVED: t('applicant.statusApproved'),
                REJECTED: t('applicant.statusRejected'),
                REVISION_REQUIRED: t('applicant.statusRevisionRequired'),
              } as Record<string, string>)[applicant?.status || 'REGISTERED'] || t('applicant.statusRegistered')}
            </span>
          </div>
          {applicant?.reviewNotes && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <p className="text-sm text-gray-700">
                <strong>{t('applicant.reviewNotes')}:</strong> {applicant.reviewNotes}
              </p>
            </div>
          )}
        </div>

        {/* Steps */}
        <div className="space-y-6">
          {/* Step 1: Download Script */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {t('applicant.step1')}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('applicant.step1Description')}
                </p>
                {applicant?.scriptDownloadedAt && (
                  <p className="text-sm text-green-600 mb-4">
                    ✓ {t('applicant.downloadedOn')} {new Date(applicant.scriptDownloadedAt).toLocaleDateString()}
                    {applicant.scriptVersion && ` (${applicant.scriptVersion})`}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleViewScript(latestVersion || 'v1.0')}
                className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('applicant.downloadScript')}
              </button>
            </div>
          </div>

          {/* Step 2: Qualification - Video OR Interview */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('applicant.step2')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Wähle, wie du dich qualifizieren möchtest:
            </p>

            {applicant?.status === 'REVISION_REQUIRED' && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800 font-medium">{t('applicant.revisionRequired')}</p>
                {applicant.reviewNotes && (
                  <p className="text-sm text-yellow-700 mt-1">{applicant.reviewNotes}</p>
                )}
              </div>
            )}

            {/* Show interview details if already scheduled */}
            {applicant?.status === 'INTERVIEW_SCHEDULED' && applicant?.interviewScheduledAt && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-md">
                <p className="text-sm font-semibold text-indigo-900 mb-1">✓ Interview gebucht</p>
                <p className="text-sm text-indigo-700">
                  Termin: {new Date(applicant.interviewScheduledAt).toLocaleString('de-DE', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })} Uhr
                </p>
                <p className="text-xs text-indigo-600 mt-2">
                  Du erhältst den Meeting-Link per Email.
                </p>
              </div>
            )}

            {/* Show video upload success */}
            {applicant?.videoUploadedAt && applicant?.status !== 'REVISION_REQUIRED' && applicant?.status !== 'INTERVIEW_SCHEDULED' && (
              <div className="text-sm text-green-600">
                ✓ {t('applicant.videoUploadedOn')} {new Date(applicant.videoUploadedAt).toLocaleDateString()}
              </div>
            )}

            {/* Show choice if not yet qualified */}
            {(applicant?.status === 'REGISTERED' || applicant?.status === 'REVISION_REQUIRED') && (
              <div>
                {/* Choice tabs */}
                {!qualificationChoice && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => setQualificationChoice('video')}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
                    >
                      <div className="text-lg font-medium text-gray-900 mb-1">📹 Video hochladen</div>
                      <p className="text-sm text-gray-600">
                        Nimm ein kurzes Video auf, in dem du den Verkaufspitch vorstellst.
                      </p>
                    </button>
                    <button
                      onClick={() => setQualificationChoice('interview')}
                      className="p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all text-left"
                    >
                      <div className="text-lg font-medium text-gray-900 mb-1">🎥 Interview buchen</div>
                      <p className="text-sm text-gray-600">
                        Buche ein 15-Minuten Interview per Video-Call mit einem unserer Interviewer.
                      </p>
                    </button>
                  </div>
                )}

                {/* Video upload form */}
                {qualificationChoice === 'video' && (
                  <div>
                    <button
                      onClick={() => setQualificationChoice(null)}
                      className="text-sm text-gray-500 hover:text-gray-700 mb-3"
                    >
                      ← Zurück zur Auswahl
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleFileSelect}
                      disabled={videoUploadMutation.isPending}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {uploadProgress && (
                      <p className="mt-2 text-sm text-gray-600">{uploadProgress}</p>
                    )}
                  </div>
                )}

                {/* Interview booking */}
                {qualificationChoice === 'interview' && (
                  <div>
                    <button
                      onClick={() => setQualificationChoice(null)}
                      className="text-sm text-gray-500 hover:text-gray-700 mb-3"
                    >
                      ← Zurück zur Auswahl
                    </button>
                    <BookInterviewPage onBooked={() => refetch()} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 3: Wait for Review */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('applicant.step3')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('applicant.step3Description')}
            </p>
          </div>
        </div>

        {/* Promo Video */}
        <div className="mt-6">
          <video
            src={promoVideo}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover rounded-lg shadow-lg"
          />
        </div>
      </main>
      {confirmModal}
    </div>
  )
}
